import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { RadioGroup } from '@anyui/core';
import { cn } from '@anytag/shared-utils';
import { Radio, Switch } from '@anytag/shared-atoms';

export interface TableSegmentOption<T extends string> {
  value: T;
  label?: ReactNode;
  loading?: boolean;
}

export interface TableSegmentProps<T extends string> {
  values: T[];
  title?: ReactNode;
  className?: string;
  type: 'radio' | 'switch';
  onChange: (value: T) => void;
  options: TableSegmentOption<T>[];
}

export const TableSegment = <T extends string>({
  type,
  title,
  values,
  options,
  onChange,
  className
}: TableSegmentProps<T>) => {
  const { t } = useTranslation();

  const isRadio = type === 'radio';

  return (
    <div className={cn('box-border flex items-center bg-white', className)}>
      <label className="mr-2 text-base font-semibold">{title || t('General.Day Segment')}</label>
      {isRadio ? (
        <RadioGroup value={values?.at(0)} onChange={(value) => onChange(value as T)}>
          {options.map(({ value, label, loading }) => (
            <Radio key={value} label={label} value={value.toString()} disabled={loading} />
          ))}
        </RadioGroup>
      ) : (
        <>
          {options.map((option, index) => {
            const { value, label, loading } = option;

            return (
              <div key={value} className={cn('flex items-center gap-1', index === 0 ? 'ml-5' : 'ml-4')}>
                <p className="text-base font-normal">{label}</p>
                <Switch loading={loading} onChange={() => onChange(value)} checked={values?.includes(value)} />
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};
